import { useState, useRef } from 'react';
import { supabase } from '../../../lib/supabase';

interface ManualManagerProps {
  manualUrl: string;
  moduleId: string;
  onChange: (url: string) => void;
}

export default function ManualManager({ manualUrl, moduleId, onChange }: ManualManagerProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [urlInput, setUrlInput] = useState(manualUrl);
  const [previewMode, setPreviewMode] = useState<'upload' | 'url'>('upload');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (file: File) => {
    if (!file || file.type !== 'application/pdf') {
      setUploadError('Solo se aceptan archivos PDF');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setUploadError('El archivo no puede superar los 50 MB');
      return;
    }
    setUploading(true);
    setUploadError('');
    try {
      const ext = 'pdf';
      const filePath = `${moduleId}/manual/${Date.now()}.${ext}`;
      const { error: uploadErr } = await supabase.storage
        .from('module-resources')
        .upload(filePath, file, { upsert: true, contentType: 'application/pdf' });
      if (uploadErr) throw uploadErr;
      const { data: urlData } = supabase.storage
        .from('module-resources')
        .getPublicUrl(filePath);
      onChange(urlData.publicUrl);
      setUrlInput(urlData.publicUrl);
    } catch (e) {
      setUploadError('Error al subir el archivo. Inténtalo de nuevo.');
      console.error(e);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  };

  const handleUrlChange = (val: string) => {
    setUrlInput(val);
    onChange(val.trim());
  };

  const handleRemove = () => {
    onChange('');
    setUrlInput('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const fileName = manualUrl
    ? manualUrl.split('/').pop()?.split('?')[0] || 'manual.pdf'
    : null;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-[#8B9D83] text-[10px] uppercase tracking-[3px] mb-1">Manual del Módulo (PDF)</p>
        <p className="text-[#F8F6F3]/40 text-sm">
          El PDF se visualiza directamente en la plataforma con visor propio. Sin descarga, con pantalla completa.
        </p>
      </div>

      {/* Mode toggle */}
      <div className="flex gap-2">
        {(['upload', 'url'] as const).map((mode) => (
          <button
            key={mode}
            onClick={() => setPreviewMode(mode)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-medium cursor-pointer transition-all duration-200 whitespace-nowrap"
            style={previewMode === mode
              ? { background: 'rgba(139,157,131,0.15)', color: '#8B9D83', border: '1px solid rgba(139,157,131,0.30)' }
              : { background: 'rgba(248,246,243,0.04)', color: 'rgba(248,246,243,0.40)', border: '1px solid rgba(248,246,243,0.08)' }
            }
          >
            <i className={mode === 'upload' ? 'ri-upload-cloud-line' : 'ri-link-m'} />
            {mode === 'upload' ? 'Subir archivo' : 'URL directa'}
          </button>
        ))}
      </div>

      {/* Current file status */}
      {manualUrl && (
        <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'rgba(139,157,131,0.08)', border: '1px solid rgba(139,157,131,0.20)' }}>
          <div className="w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: 'rgba(139,157,131,0.12)' }}>
            <i className="ri-file-pdf-2-line text-[#8B9D83] text-lg" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[#8B9D83] text-xs font-semibold">PDF asignado</p>
            <p className="text-[#F8F6F3]/30 text-[11px] truncate mt-0.5">{fileName}</p>
          </div>
          <button
            onClick={handleRemove}
            className="w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer hover:opacity-70 transition-opacity flex-shrink-0"
            style={{ background: 'rgba(201,123,99,0.12)', color: '#C97B63' }}
          >
            <i className="ri-close-line text-xs" />
          </button>
        </div>
      )}

      {/* Upload zone */}
      {previewMode === 'upload' && (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileUpload(f); }}
          />
          <div
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            className="flex flex-col items-center justify-center py-12 rounded-2xl gap-4 cursor-pointer transition-all duration-200 hover:opacity-80"
            style={{ background: 'rgba(248,246,243,0.02)', border: '2px dashed rgba(139,157,131,0.25)' }}
          >
            {uploading ? (
              <>
                <i className="ri-loader-4-line animate-spin text-[#8B9D83] text-3xl" />
                <p className="text-[#F8F6F3]/50 text-sm">Subiendo archivo...</p>
              </>
            ) : (
              <>
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl" style={{ background: 'rgba(139,157,131,0.10)', border: '1px solid rgba(139,157,131,0.20)' }}>
                  <i className="ri-upload-cloud-2-line text-[#8B9D83] text-2xl" />
                </div>
                <div className="text-center">
                  <p className="text-[#F8F6F3]/60 text-sm font-medium">Arrastra el PDF aquí o haz clic</p>
                  <p className="text-[#F8F6F3]/25 text-xs mt-1">Solo PDF · Máx. 50 MB</p>
                </div>
              </>
            )}
          </div>
          {uploadError && (
            <p className="mt-2 text-[#C97B63] text-xs flex items-center gap-1.5">
              <i className="ri-error-warning-line" /> {uploadError}
            </p>
          )}
        </div>
      )}

      {/* URL input */}
      {previewMode === 'url' && (
        <div className="flex flex-col gap-2">
          <label className="text-[#F8F6F3]/50 text-xs uppercase tracking-[2px]">URL directa del PDF</label>
          <input
            type="text"
            value={urlInput}
            onChange={(e) => handleUrlChange(e.target.value)}
            placeholder="https://... (URL pública del PDF)"
            className="w-full px-4 py-3 rounded-xl text-sm text-[#F8F6F3] placeholder-[#F8F6F3]/20 outline-none"
            style={{ background: 'rgba(248,246,243,0.06)', border: '1px solid rgba(248,246,243,0.10)' }}
          />
          <p className="text-[#F8F6F3]/25 text-[11px]">
            Pega la URL directa del PDF (desde Supabase Storage, Cloudflare R2, S3, etc.)
          </p>
        </div>
      )}

      {/* Security note */}
      <div className="flex items-start gap-3 p-4 rounded-xl" style={{ background: 'rgba(139,157,131,0.06)', border: '1px solid rgba(139,157,131,0.12)' }}>
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
          <i className="ri-shield-check-line text-[#8B9D83] text-sm" />
        </div>
        <p className="text-[#F8F6F3]/40 text-xs leading-relaxed">
          El PDF se visualiza con el visor integrado de la plataforma. Los alumnos no pueden descargarlo directamente ni acceder a la barra del navegador.
        </p>
      </div>
    </div>
  );
}
